/* eslint-disable react/prop-types */
import React, { forwardRef } from "react";
import { useFormContext } from "react-hook-form";

/**
 * Drop-in form control that handles label + register + red border + inline message.
 *
 * Usage (inside <FormProvider>):
 *
 *   <Field name="email" label="Email" type="email" rules={{ required: "Email is required" }} />
 *   <Field name="bio" as="textarea" rows={4} label="Bio" />
 *   <Field name="country" as="select" label="Country">
 *     <option value="">Select</option>
 *     <option value="bd">Bangladesh</option>
 *   </Field>
 *
 * All HTML props (placeholder, type, autoComplete, disabled, etc.) pass through.
 * Server errors set via setError("email", { type: "server", message }) automatically render here.
 */

const baseInputClass =
  "mt-1 p-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200";
const okBorder = "border-gray-300 focus:border-gray-200 focus:ring-gray-300";
const errBorder = "border-red-500 focus:border-red-500 focus:ring-red-300 bg-red-50";

export const Field = forwardRef(function Field(
  {
    name,
    label,
    as = "input",
    rules,
    className = "",
    inputClassName = "",
    labelClassName = "",
    children,
    hint,
    rightAdornment,
    ...rest
  },
  forwardedRef
) {
  const ctx = useFormContext();
  if (!ctx) {
    // eslint-disable-next-line no-console
    console.error(
      `[Field] "${name}" was rendered outside a <FormProvider>. Wrap your form with <FormProvider {...methods}>.`
    );
    return null;
  }

  const {
    register,
    formState: { errors },
  } = ctx;

  // Support nested names like "address.city"
  const error = name.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), errors);
  const hasError = Boolean(error);
  const message = error?.message;

  const registered = register(name, rules);
  const mergedRef = (el) => {
    registered.ref(el);
    if (typeof forwardedRef === "function") forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  const sharedProps = {
    id: name,
    "aria-invalid": hasError || undefined,
    "aria-describedby": hasError ? `${name}-error` : hint ? `${name}-hint` : undefined,
    className: `${baseInputClass} ${hasError ? errBorder : okBorder} ${inputClassName}`.trim(),
    ...rest,
    ...registered,
    ref: mergedRef,
  };

  let control;
  if (as === "textarea") {
    control = <textarea {...sharedProps}>{children}</textarea>;
  } else if (as === "select") {
    control = <select {...sharedProps}>{children}</select>;
  } else {
    control = <input {...sharedProps} type={rest.type || "text"} />;
  }

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium text-gray-700 ${labelClassName}`.trim()}
        >
          {label}
        </label>
      )}
      {rightAdornment ? (
        <div className="relative">
          {control}
          {rightAdornment}
        </div>
      ) : (
        control
      )}
      {hasError ? (
        <p id={`${name}-error`} className="text-sm mt-1 ml-1 text-red-600" role="alert">
          {message || "Invalid value."}
        </p>
      ) : hint ? (
        <p id={`${name}-hint`} className="text-xs mt-1 ml-1 text-gray-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

/**
 * Renders a form-wide error banner — useful for non_field_errors that aren't tied to an input.
 * Just call setNonFieldError("Message") via setError("root.serverError", {...}) and it shows.
 */
export function FormBanner({ name = "root.serverError" }) {
  const ctx = useFormContext();
  if (!ctx) return null;
  const { formState } = ctx;
  const path = name.split(".");
  const err = path.reduce((acc, k) => (acc ? acc[k] : undefined), formState.errors);
  if (!err?.message) return null;
  return (
    <div
      role="alert"
      className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
    >
      {err.message}
    </div>
  );
}
